import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CreateVehicleInput } from '@etnrlz/shared';
import { useCreateVehicle, useVehicles } from '../../lib/queries';
import { Badge, Button, EmptyState, Field, Input, Loading, PageHeader, Textarea } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { PhotoUpload } from '../../components/PhotoUpload';
import { Icon } from '../../components/icons';
import { vehicleStatusMeta } from '../../components/status';
import { formatKm } from '../../lib/format';

export function VehiclesPage() {
  const { data, isLoading } = useVehicles();
  const [open, setOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title="Veículos"
        subtitle={data ? `${data.length} na frota` : undefined}
        actions={
          <Button onClick={() => setOpen(true)}>
            <Icon name="plus" className="h-4 w-4" /> Novo veículo
          </Button>
        }
      />

      {isLoading ? (
        <Loading />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Nenhum veículo" hint="Cadastre o primeiro carro da frota." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.map((v) => {
            const meta = vehicleStatusMeta[v.status];
            return (
              <Link
                key={v.id}
                to={`/admin/vehicles/${v.id}`}
                className="card overflow-hidden p-0 hover:border-border-strong transition-colors group"
              >
                <div className="aspect-[16/10] bg-surface-2 overflow-hidden">
                  {v.fotoCapa ? (
                    <img
                      src={v.fotoCapa}
                      alt={v.apelido}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-muted">
                      <Icon name="car" className="h-10 w-10" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold truncate">{v.apelido}</div>
                    <Badge color={meta.color}>{meta.label}</Badge>
                  </div>
                  <div className="text-sm text-muted truncate">
                    {v.marca} {v.modelo} · {v.ano}
                  </div>
                  <div className="num text-xs text-muted mt-2">{formatKm(v.kmAtual)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <NewVehicleModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function NewVehicleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateVehicle();
  const [form, setForm] = useState<CreateVehicleInput>({
    apelido: '',
    marca: '',
    modelo: '',
    ano: new Date().getFullYear(),
  });
  const set = (k: keyof CreateVehicleInput, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    await create.mutateAsync(form);
    onClose();
    setForm({ apelido: '', marca: '', modelo: '', ano: new Date().getFullYear() });
  };

  const valid = form.apelido && form.marca && form.modelo;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo veículo"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!valid || create.isPending}>
            {create.isPending ? 'Salvando…' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Apelido">
            <Input value={form.apelido} onChange={(e) => set('apelido', e.target.value)} placeholder="Staysideways #16" />
          </Field>
        </div>
        <Field label="Marca">
          <Input value={form.marca} onChange={(e) => set('marca', e.target.value)} placeholder="Nissan" />
        </Field>
        <Field label="Modelo">
          <Input value={form.modelo} onChange={(e) => set('modelo', e.target.value)} placeholder="350Z" />
        </Field>
        <Field label="Ano">
          <Input type="number" value={form.ano} onChange={(e) => set('ano', Number(e.target.value))} />
        </Field>
        <Field label="Placa">
          <Input value={form.placa ?? ''} onChange={(e) => set('placa', e.target.value)} />
        </Field>
        <Field label="Cor">
          <Input value={form.cor ?? ''} onChange={(e) => set('cor', e.target.value)} />
        </Field>
        <Field label="Km atual">
          <Input type="number" value={form.kmAtual ?? 0} onChange={(e) => set('kmAtual', Number(e.target.value))} />
        </Field>
        <div className="col-span-2">
          <Field label="Foto de capa">
            <PhotoUpload value={form.fotoCapa} onChange={(url) => set('fotoCapa', url)} prefix="vehicles" />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Observações">
            <Textarea value={form.observacoes ?? ''} onChange={(e) => set('observacoes', e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
